/* eslint max-lines: ["error", {max: 280}] -- web shim has to mirror the native SDK's full export surface */
import "@sendbird/uikit-react/dist/index.css";
import "@/crossPlatform/Sendbird/sendbird.web.css";

import type { PropsWithChildren, ReactElement } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { SendbirdChatWith } from "@sendbird/chat";
import {
  ConnectionHandler,
  PushTriggerOption,
  RestrictionType,
  Role,
  UserOnlineState,
} from "@sendbird/chat";
import type {
  GroupChannel,
  GroupChannelModule,
  Member,
} from "@sendbird/chat/groupChannel";
import { GroupChannelHandler, MemberState } from "@sendbird/chat/groupChannel";
import type {
  FileMessage,
  FileMessageCreateParams,
  MultipleFilesMessage,
  Sender,
  UserMessage,
  UserMessageCreateParams,
} from "@sendbird/chat/message";
import type { OpenChannelModule } from "@sendbird/chat/openChannel";
import { useGroupChannel } from "@sendbird/uikit-chat-hooks";
import { useSendbird } from "@sendbird/uikit-react";
import { GroupChannel as UikitGroupChannel } from "@sendbird/uikit-react/GroupChannel";
import type { Message as UikitMessage } from "@sendbird/uikit-react/GroupChannel/components/Message";
import { useGroupChannel as useUikitGroupChannel } from "@sendbird/uikit-react/GroupChannel/context";
import type {
  ClipboardServiceInterface,
  FileServiceInterface,
  MediaServiceInterface,
  NotificationServiceInterface,
  PlayerServiceInterface,
  RecorderServiceInterface,
  SendbirdUIKitContainerProps,
} from "@sendbird/uikit-react-native";
import type { BottomSheetItem } from "@sendbird/uikit-react-native-foundation";
import isNil from "lodash/isNil";

import { ReactionAddons } from "@/crossPlatform/Sendbird/ReactionAddons.web";

// Mirrors @sendbird/uikit-react's internal SendableMessageType (not a public export).
type SendableMessage = FileMessage | MultipleFilesMessage | UserMessage;

const noop = (): void => undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- stub components accept arbitrary props
const noopComponent = (_props?: any): null => null;

const Box = noopComponent;
/* eslint-disable @typescript-eslint/consistent-type-assertions -- stubs cast empty objects to SDK interface types; web never calls these so the cast is safe */
const createExpoClipboardService = (_mod?: object): ClipboardServiceInterface =>
  ({}) as unknown as ClipboardServiceInterface;
const createExpoFileService = (_mods?: object): FileServiceInterface =>
  ({}) as unknown as FileServiceInterface;
const createExpoMediaService = (_mod?: object): MediaServiceInterface =>
  ({}) as unknown as MediaServiceInterface;
const createExpoNotificationService = (
  _mod?: object,
): NotificationServiceInterface =>
  ({}) as unknown as NotificationServiceInterface;
const createExpoPlayerService = (_mod?: object): PlayerServiceInterface =>
  ({}) as unknown as PlayerServiceInterface;
const createExpoRecorderService = (_mod?: object): RecorderServiceInterface =>
  ({}) as unknown as RecorderServiceInterface;
/* eslint-enable @typescript-eslint/consistent-type-assertions */

// Mirrors native SDK's GroupChannelProps["Input"] shape for cross-platform compatibility.
// Web implementation only uses onPressSend(File|User)Message; remaining fields are stubs.
interface SendbirdInputProps {
  AttachmentsButton?: () => null | ReactElement;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- matches native SDK naming
  inputDisabled?: boolean;
  onPressSendFileMessage: (
    params: FileMessageCreateParams,
  ) => Promise<FileMessage>;
  onPressSendUserMessage: (
    params: UserMessageCreateParams,
  ) => Promise<UserMessage>;

  onPressUpdateFileMessage?: (
    params: FileMessageCreateParams,
  ) => Promise<FileMessage>;

  onPressUpdateUserMessage?: (
    params: UserMessageCreateParams,
  ) => Promise<UserMessage>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches native SDK's flexible TextInput props shape
  partialTextInputProps?: Record<string, any>;
  shouldRenderInput?: boolean;

  SuggestedMentionList?: () => null | ReactElement;
}

// Web-only type to mirror native SDK's GroupChannelProps shape.
// Consumers extract SendbirdInputProps via GroupChannelProps["Input"].
interface GroupChannelProps {
  Input: SendbirdInputProps;
}

type RenderMessageProps = Parameters<typeof UikitMessage>[0];

type CreatedFragmentProps = {
  channel: GroupChannel;
  // eslint-disable-next-line react/no-unused-prop-types -- mirrors native SDK shape; consumer passes it
  onChannelDeleted: () => void;
  onPressHeaderLeft: () => void;
  // eslint-disable-next-line react/no-unused-prop-types -- mirrors native SDK shape; consumer passes it
  onPressHeaderRight: () => void;
  renderMessage: (props: RenderMessageProps) => ReactElement;
};

const createGroupChannelFragment = ({
  Header,
  Input,
  StatusLoading,
}: {
  Header: () => ReactElement;
  Input: (props: SendbirdInputProps) => ReactElement;
  StatusLoading: () => null | ReactElement;
}) => {
  const InputSlot = () => {
    const {
      actions: { sendFileMessage, sendUserMessage },
    } = useUikitGroupChannel();
    return (
      <Input
        onPressSendFileMessage={sendFileMessage}
        onPressSendUserMessage={sendUserMessage}
      />
    );
  };

  // passing a bare StatusLoading results in the 'Type your message' box starting at the very top and flickering to the bottom of the screen
  // to prevent that, and have a non-zero height placeholder for chat history, ".sendbird-conversation_messages" is used and overridden
  const PlaceholderLoader = () => (
    <div
      className="sendbird-conversation__messages"
      style={{ display: "flex", flexDirection: "column" }}
    >
      {StatusLoading()}
    </div>
  );

  const GroupChannelFragmentComponent = ({
    channel,
    onPressHeaderLeft,
    renderMessage,
  }: CreatedFragmentProps) => (
    <SendbirdWebBottomSheetProvider>
      <UikitGroupChannel
        channelUrl={channel.url}
        onBackClick={onPressHeaderLeft}
        renderChannelHeader={Header}
        renderMessage={renderMessage}
        renderMessageInput={InputSlot}
        renderPlaceholderLoader={PlaceholderLoader}
      />
    </SendbirdWebBottomSheetProvider>
  );
  return GroupChannelFragmentComponent;
};

const GroupChannelMessageRenderer = noopComponent;
const SendbirdUIKitContainer = noopComponent;

const BottomSheetContext = createContext<{
  openSheet: (item: BottomSheetItem) => void;
}>({
  openSheet: noop,
});

// mirrors native's usage/pattern in GroupChannelFragment
const SendbirdWebBottomSheetProvider = ({ children }: PropsWithChildren) => {
  const [activeSheet, setActiveSheet] = useState<BottomSheetItem | null>(null);

  const openSheet = useCallback(
    (item: BottomSheetItem) => setActiveSheet(item),
    [],
  );

  const handleClose = useCallback(async () => setActiveSheet(null), []);

  const value = useMemo(() => ({ openSheet }), [openSheet]);

  return (
    <BottomSheetContext.Provider value={value}>
      {children}
      {!isNil(activeSheet?.HeaderComponent) && (
        <activeSheet.HeaderComponent onClose={handleClose} />
      )}
    </BottomSheetContext.Provider>
  );
};

const useBottomSheet = () => useContext(BottomSheetContext);

const useConnection = () => ({
  // eslint-disable-next-line max-params -- mirrors @sendbird/uikit-react-native signature
  connect: (
    _userId?: string,
    _options?: { accessToken?: string },
  ): Promise<void> => Promise.resolve(),
});

type WebSdk = SendbirdChatWith<[GroupChannelModule, OpenChannelModule]>;

const useSendbirdChat = () => {
  const { state } = useSendbird();
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- to conform to Sendbird typing
  const sdk = state.stores.sdkStore.sdk as unknown as WebSdk;
  return { sdk };
};

type UseConnection = ReturnType<typeof useConnection>;
type SendbirdChatContextType = ReturnType<typeof useSendbirdChat>;
type SendbirdChatSDK = SendbirdChatContextType["sdk"];

export type {
  BottomSheetItem,
  FileMessage,
  GroupChannel,
  GroupChannelProps,
  Member,
  SendableMessage,
  SendbirdChatContextType,
  SendbirdChatSDK,
  SendbirdInputProps,
  SendbirdUIKitContainerProps,
  Sender,
  UseConnection,
};

export {
  Box,
  ConnectionHandler,
  createExpoClipboardService,
  createExpoFileService,
  createExpoMediaService,
  createExpoNotificationService,
  createExpoPlayerService,
  createExpoRecorderService,
  createGroupChannelFragment,
  GroupChannelHandler,
  GroupChannelMessageRenderer,
  MemberState,
  PushTriggerOption,
  ReactionAddons,
  RestrictionType,
  Role,
  SendbirdUIKitContainer,
  SendbirdWebBottomSheetProvider,
  useBottomSheet,
  useConnection,
  useGroupChannel,
  UserOnlineState,
  useSendbirdChat,
};
